import { useCallback, useRef, useState } from 'react';
import { useDispatch } from '../state/AppContext';
import { GO_TO_INPUT } from '../state/actions';
import CollisionCanvas from '../galaxy/CollisionCanvas';
import exampleCollisionData from '../galaxy/exampleCollisionData';
import Header from './Header';
import '../styles/universe-preview.css';
import '../styles/collision.css';

const PREVIEW_TOUR_STEPS = [
  {
    title: 'Collide Universes',
    text: 'When you collide with a friend, your music universes merge into a shared map. You\'ll see where your tastes overlap and where they diverge. Try pinching to zoom and dragging to pan!',
    icon: 'collide',
  },
  {
    title: 'Core Overlap',
    text: 'Artists you both love appear in the center — your musical common ground. The more overlap, the stronger your musical connection.',
    icon: 'overlap',
  },
  {
    title: 'Shared Frontier',
    text: 'These are new recommendations generated from both your libraries combined. Music neither of you have heard yet, but both would probably love.',
    icon: 'frontier',
  },
  {
    title: 'Discover Together',
    text: 'Incoming signals are artists your friend knows that you might love. Outgoing signals flow the other way. It\'s a two-way exchange of music discovery.',
    icon: 'discover',
  },
];

function StepIcon({ icon }) {
  switch (icon) {
    case 'collide':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <circle cx="9" cy="12" r="6" />
          <circle cx="15" cy="12" r="6" />
        </svg>
      );
    case 'overlap':
      return (
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <circle cx="9" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
          <circle cx="15" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
          <path d="M12 7.5c1.5 1.2 2.2 2.7 2.2 4.5s-.7 3.3-2.2 4.5c-1.5-1.2-2.2-2.7-2.2-4.5s.7-3.3 2.2-4.5z" fill="currentColor" opacity="0.6" />
        </svg>
      );
    case 'frontier':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      );
    case 'discover':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      );
    default:
      return null;
  }
}

function getBadgeLabel(node) {
  if (node.zone === 'core_overlap') return 'Core Overlap';
  if (node.zone === 'your_artists') return 'Your Artist';
  if (node.zone === 'friend_artists') return "Friend's Artist";
  if (node.zone === 'shared_frontier') return 'Shared Frontier';
  if (node.zone === 'your_exploration') return 'Incoming Signal';
  if (node.zone === 'friend_exploration') return 'Outgoing Signal';
  return 'Artist';
}

function getBadgeClass(node) {
  if (node.zone === 'core_overlap') return 'badge-favorite';
  if (node.zone === 'shared_frontier') return 'badge-rec';
  return 'badge-discovered';
}

function CollisionPreview() {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showTour, setShowTour] = useState(true);

  const handleBack = useCallback(() => {
    dispatch({ type: GO_TO_INPUT });
  }, [dispatch]);

  const handleSelectNode = useCallback((node) => {
    if (node && !node._isClusterCenter && !node._isSuperclusterCenter) {
      setSelectedNode(node);
      setShowTour(false);
    } else {
      setSelectedNode(null);
    }
  }, []);

  const handleHoverNode = useCallback(() => {}, []);

  const handleCloseCard = useCallback(() => {
    setSelectedNode(null);
    setShowTour(true);
  }, []);

  const isLastStep = currentStep === PREVIEW_TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const step = PREVIEW_TOUR_STEPS[currentStep];

  const emptyArr = [];

  return (
    <div className="universe-preview-view">
      {/* Header */}
      <div className="collision-header-overlay">
        <Header showBack onBack={handleBack} mapName="Collision Preview" />
      </div>

      {/* Full interactive canvas */}
      <CollisionCanvas
        ref={canvasRef}
        collisionData={exampleCollisionData}
        favorites={emptyArr}
        discoveredArtists={emptyArr}
        dislikes={emptyArr}
        onSelectNode={handleSelectNode}
        onHoverNode={handleHoverNode}
      />

      {/* "Example" badge */}
      <div className="universe-preview-badge">
        <span className="universe-preview-badge-dot" />
        Example Collision — Explore with pinch & drag
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls universe-preview-zoom">
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(1.4)}
          title="Zoom in"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(0.7)}
          title="Zoom out"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.resetView()}
          title="Reset view"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M21 3l-7 7" />
            <path d="M3 21l7-7" />
          </svg>
        </button>
      </div>

      {/* Selected node info card */}
      {selectedNode && !selectedNode._isClusterCenter && (
        <div className="universe-preview-node-card">
          <button className="universe-card-close" onClick={handleCloseCard}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="universe-card-header">
            <div
              className="universe-card-image universe-card-image-placeholder"
              style={{ width: 44, height: 44 }}
            >
              {selectedNode.name?.charAt(0) || '?'}
            </div>
            <div className="universe-card-info">
              <h4 className="universe-card-name">{selectedNode.name}</h4>
              <div className="universe-card-badges">
                <span className={`universe-card-badge ${getBadgeClass(selectedNode)}`}>
                  {getBadgeLabel(selectedNode)}
                </span>
              </div>
            </div>
          </div>
          <p className="universe-preview-node-hint">
            In a real collision, you can preview tracks, see shared genres, and explore zone by zone.
          </p>
        </div>
      )}

      {/* Tour card overlay */}
      {showTour && (
        <div className="universe-preview-tour-card">
          <div className="universe-preview-tour-icon">
            <StepIcon icon={step.icon} />
          </div>
          <div className="universe-preview-tour-header">
            <span className="universe-preview-tour-title">{step.title}</span>
            <span className="universe-preview-tour-counter">
              {currentStep + 1} / {PREVIEW_TOUR_STEPS.length}
            </span>
          </div>
          <p className="universe-preview-tour-text">{step.text}</p>
          <div className="universe-preview-tour-actions">
            <button className="universe-preview-tour-skip" onClick={handleBack}>
              Close
            </button>
            <div className="universe-preview-tour-nav">
              {!isFirstStep && (
                <button
                  className="universe-preview-tour-back"
                  onClick={() => setCurrentStep((s) => s - 1)}
                >
                  Back
                </button>
              )}
              <button
                className="universe-preview-tour-next"
                onClick={isLastStep ? handleBack : () => setCurrentStep((s) => s + 1)}
              >
                {isLastStep ? 'Get Started' : 'Next'}
              </button>
            </div>
          </div>
          {/* Step dots */}
          <div className="universe-preview-tour-dots">
            {PREVIEW_TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                className={`universe-preview-tour-dot${i === currentStep ? ' active' : ''}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CollisionPreview;
